import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, API, fmtINR, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  LogOut, Plus, Trash2, Star, Edit3, RefreshCw, Save, Download, Upload, Tag,
} from "lucide-react";
import PropertyEditor from "./PropertyEditor";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [properties, setProperties] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [apiLogs, setApiLogs] = useState([]);
  const [editingSlug, setEditingSlug] = useState(null);
  const [activeTab, setActiveTab] = useState("feed");
  const [syncing, setSyncing] = useState(false);
  const [diffing, setDiffing] = useState(false);

  const openEditor = (slug) => { setActiveTab("properties"); setEditingSlug(slug); };
  const closeEditor = () => setEditingSlug(null);

  useEffect(() => {
    const token = localStorage.getItem("vyg_token");
    if (!token) { nav("/admin/login"); return; }
    api.get("/admin/me").then(({ data }) => setMe(data)).catch(() => { localStorage.removeItem("vyg_token"); nav("/admin/login"); });
    reloadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadAll = () => {
    api.get("/properties").then(({ data }) => setProperties(data));
    api.get("/admin/reviews").then(({ data }) => setReviews(data));
    api.get("/admin/contact-messages").then(({ data }) => setMessages(data));
    api.get("/admin/hostify-calls").then(({ data }) => setApiLogs(data));
  };

  const syncHostify = async (slug) => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/hostify/sync", slug ? { slug } : {});
      toast.success(`Synced ${data.count} listing${data.count === 1 ? "" : "s"}${data.failed?.length ? ` · ${data.failed.length} failed` : ""}`);
      reloadAll();
    } catch (e) { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };

  const diffHostify = async () => {
    setDiffing(true);
    try {
      const { data } = await api.post("/admin/hostify/diff");
      const added = data.added?.length || 0; const archived = data.archived?.length || 0;
      toast.success(`Diff complete · +${added} new · ${archived} archived`);
      reloadAll();
    } catch { toast.error("Diff failed — check Hostify API logs."); }
    finally { setDiffing(false); }
  };

  const logout = () => { localStorage.removeItem("vyg_token"); nav("/admin/login"); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-parchment">
      <Helmet><title>Admin Dashboard · Vyagram</title></Helmet>
      <header className="border-b border-forest/10 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-10">
          <div>
            <p className="font-display text-xl text-forest">Vyagram Admin</p>
            <p className="text-xs text-noir/60">{me?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-noir/70 hover:text-forest">View site →</Link>
            <button onClick={logout} className="vyg-pill" data-testid="admin-logout"><LogOut size={14}/> Log out</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10">
        {editingSlug ? (
          <PropertyEditor slug={editingSlug} onClose={closeEditor} onSaved={() => { reloadAll(); }} />
        ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="feed" data-testid="tab-feed">Booking feed</TabsTrigger>
            <TabsTrigger value="properties" data-testid="tab-properties">Properties ({properties.length})</TabsTrigger>
            <TabsTrigger value="tags" data-testid="tab-tags">Tags</TabsTrigger>
            <TabsTrigger value="seo-tags" data-testid="tab-seo-tags">SEO tags</TabsTrigger>
            <TabsTrigger value="faqs" data-testid="tab-faqs">FAQs</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews ({reviews.length})</TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-config">Site config</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
            <TabsTrigger value="curation" data-testid="tab-curation">Curation</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messages ({messages.length})</TabsTrigger>
            <TabsTrigger value="api-logs" data-testid="tab-api-logs">Hostify API</TabsTrigger>
          </TabsList>

          <TabsContent value="feed"><BookingFeed /></TabsContent>

          <TabsContent value="properties">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-noir/70">Click any property to edit photos, videos, tags, house rules and hero image.</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={diffHostify} disabled={diffing} data-testid="hostify-diff"
                  className="vyg-pill disabled:opacity-60" title="Detect listings added or removed in Hostify">
                  <RefreshCw size={14} className={diffing ? "animate-spin" : ""}/> {diffing ? "Diffing…" : "Detect Hostify add/remove"}
                </button>
                <button onClick={() => syncHostify()} disabled={syncing} data-testid="sync-all-hostify"
                  className="vyg-pill disabled:opacity-60">
                  <RefreshCw size={14} className={syncing ? "animate-spin" : ""}/> {syncing ? "Syncing from Hostify…" : "Sync all from Hostify"}
                </button>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {properties.map((p) => (
                <div key={p.slug} className="rounded-xl border border-forest/10 bg-white p-4" data-testid={`admin-prop-${p.slug}`}>
                  <div className="flex gap-4">
                    <img src={p.hero_image} alt="" className="h-20 w-28 rounded-lg object-cover" />
                    <div className="flex-1">
                      <p className="font-display text-lg">{p.display_name}</p>
                      <p className="text-xs text-noir/60">{p.location_label}</p>
                      <div className="mt-1 flex items-center gap-3 text-xs text-noir/70">
                        <span className="inline-flex items-center gap-1"><Star size={10} className="fill-gold text-gold"/>{p.rating} · {p.review_count}</span>
                        <span>{p.bedrooms} BR · {p.guests} guests</span>
                        <span>{fmtINR(p.nightly_price_from)}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {p.collections?.slice(0, 4).map((c) => (<span key={c} className="rounded-full bg-mist px-2 py-0.5 text-[10px] text-forest">{c}</span>))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => openEditor(p.slug)} data-testid={`edit-prop-${p.slug}`}
                        className="inline-flex items-center gap-1 rounded-full border border-forest/20 bg-mist/40 px-3 py-1.5 text-xs font-medium text-forest hover:bg-mist">
                        <Edit3 size={12}/> Edit
                      </button>
                      <button onClick={() => syncHostify(p.slug)} disabled={syncing}
                        data-testid={`sync-prop-${p.slug}`}
                        className="inline-flex items-center gap-1 rounded-full border border-forest/20 bg-white px-3 py-1.5 text-xs text-forest hover:bg-mist/40 disabled:opacity-50">
                        <RefreshCw size={12} className={syncing ? "animate-spin" : ""}/> Sync
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="tags"><TagsAdmin /></TabsContent>
          <TabsContent value="seo-tags"><SeoTagsAdmin /></TabsContent>
          <TabsContent value="faqs"><GlobalFaqsAdmin /></TabsContent>
          <TabsContent value="config"><SiteConfigAdmin /></TabsContent>
          <TabsContent value="notifications"><NotificationsAdmin /></TabsContent>
          <TabsContent value="curation"><CurationAdmin /></TabsContent>

          <TabsContent value="reviews">
            <ReviewsAdmin reviews={reviews} properties={properties} onChange={reloadAll} />
          </TabsContent>

          <TabsContent value="messages">
            <div className="space-y-3">
              {messages.length === 0 && <p className="text-sm text-noir/60">No contact messages yet.</p>}
              {messages.map((m) => (
                <div key={m.id} className="rounded-xl border border-forest/10 bg-white p-5" data-testid={`msg-${m.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{m.name}</p>
                    <p className="text-xs text-noir/60">{fmtDate(m.created_at)}</p>
                  </div>
                  <p className="text-xs text-noir/60">{m.email} · {m.phone}</p>
                  <p className="mt-2 text-sm">{m.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="api-logs">
            <div className="overflow-x-auto rounded-2xl bg-white shadow-card">
              <table className="min-w-full text-xs">
                <thead className="bg-mist/60 text-left uppercase tracking-wider text-amber">
                  <tr><th className="p-3">When</th><th className="p-3">Endpoint</th><th className="p-3">Method</th><th className="p-3">Status</th><th className="p-3">Time</th><th className="p-3">Error</th><th className="p-3">Response preview</th></tr>
                </thead>
                <tbody>
                  {apiLogs.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-noir/60">No Hostify calls logged yet.</td></tr>}
                  {apiLogs.map((c) => (
                    <tr key={c.id} className="border-t border-forest/10 align-top" data-testid={`api-log-${c.id}`}>
                      <td className="p-3 whitespace-nowrap text-noir/60">{fmtDate(c.at)}<br/><span className="text-[10px]">{new Date(c.at).toLocaleTimeString()}</span></td>
                      <td className="p-3 font-mono">{c.endpoint}</td>
                      <td className="p-3">{c.method}</td>
                      <td className="p-3"><span className={`rounded-full px-2 py-0.5 font-medium ${c.status >= 200 && c.status < 300 ? "bg-forest/10 text-forest" : c.status === 0 ? "bg-red-100 text-red-700" : "bg-amber/20 text-amber"}`}>{c.status || "—"}</span></td>
                      <td className="p-3 text-noir/70">{c.elapsed_ms}ms</td>
                      <td className="p-3 text-red-700">{c.error || ""}</td>
                      <td className="p-3 font-mono text-[11px] text-noir/60 max-w-sm truncate" title={c.body_preview}>{c.body_preview?.slice(0, 140)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper: CSV downloader for authenticated admin endpoints
// ---------------------------------------------------------------------------
async function downloadCsv(path, filename) {
  const token = localStorage.getItem("vyg_token");
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) { toast.error("Export failed"); return; }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Booking Feed — live feed with filters + CSV export
// ---------------------------------------------------------------------------
function BookingFeed() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");

  const load = () => {
    const q = status ? `?status=${status}` : "";
    api.get(`/admin/booking-feed${q}`).then(({ data }) => setRows(data));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setInterval(load, 15000); return () => clearInterval(t); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const filters = [
    { value: "", label: `All (${rows.length})` },
    { value: "attempted", label: "Attempted" },
    { value: "confirmed", label: "Confirmed" },
    { value: "failed", label: "Failed" },
  ];

  const exportCsv = () => {
    const q = status ? `?status=${status}` : "";
    downloadCsv(`/admin/booking-feed/export.csv${q}`, `booking-feed${status ? `-${status}` : ""}.csv`);
  };

  return (
    <div data-testid="booking-feed">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {filters.map((f) => (
          <button key={f.value} onClick={() => setStatus(f.value)} data-testid={`feed-filter-${f.value || "all"}`}
            className={`rounded-full border px-3 py-1.5 text-sm ${status === f.value ? "border-forest bg-forest text-parchment" : "border-forest/20 bg-white text-noir/80"}`}>
            {f.label}
          </button>
        ))}
        <button onClick={exportCsv} data-testid="export-booking-csv"
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-forest/20 bg-white px-3 py-1.5 text-sm text-forest hover:bg-mist/40">
          <Download size={14}/> Export CSV
        </button>
        <span className="text-xs text-noir/50">Auto-refreshes every 15s · Email → vyagramexperiences@gmail.com</span>
      </div>
      <div className="overflow-x-auto rounded-2xl bg-white shadow-card">
        <table className="min-w-full text-sm">
          <thead className="bg-mist/60 text-left text-xs uppercase tracking-wider text-amber">
            <tr>
              <th className="p-3">Guest</th><th className="p-3">Property</th><th className="p-3">Dates</th>
              <th className="p-3">Status</th><th className="p-3">Stage</th>
              <th className="p-3">Hostify Res.</th><th className="p-3">Total</th><th className="p-3">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-noir/60">No bookings match this filter.</td></tr>}
            {rows.map((b) => (
              <tr key={b.id} className="border-t border-forest/10" data-testid={`feed-row-${b.id}`}>
                <td className="p-3">
                  <p className="font-medium">{b.guest_name || <span className="text-noir/40">—</span>}</p>
                  <p className="text-xs text-noir/60">{b.guest_email || ""} · {b.guest_phone || ""}</p>
                </td>
                <td className="p-3 text-xs">{b.property_name || b.slug}</td>
                <td className="p-3 whitespace-nowrap text-xs">{b.start_date} → {b.end_date}</td>
                <td className="p-3"><StatusBadge status={b.status} /></td>
                <td className="p-3 text-xs text-noir/60">{b.stage || "—"}</td>
                <td className="p-3 text-xs">{b.hostify_reservation_id || <span className="text-noir/40">—</span>}</td>
                <td className="p-3">{b.total ? fmtINR(b.total) : (b.quote?.total ? fmtINR(b.quote.total) : "—")}</td>
                <td className="p-3 text-xs text-noir/60 whitespace-nowrap">{b.created_at ? new Date(b.created_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tags Admin — Collections + Locations CRUD + hero image overrides + about HTML
// ---------------------------------------------------------------------------
function TagsAdmin() {
  const [collections, setCollections] = useState([]);
  const [locations, setLocations] = useState([]);
  const load = () => {
    api.get("/admin/collections").then(({ data }) => setCollections(data));
    api.get("/admin/locations").then(({ data }) => setLocations(data));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="tags-admin">
      <TagGroup title="Collections" items={collections} kind="collections" onChange={load} />
      <TagGroup title="Locations" items={locations} kind="locations" onChange={load} supportsAbout />
    </div>
  );
}

function TagGroup({ title, items, kind, onChange, supportsAbout = false }) {
  const [newSlug, setNewSlug] = useState("");
  const [newName, setNewName] = useState("");
  const [openAbout, setOpenAbout] = useState(null);
  const [aboutDraft, setAboutDraft] = useState("");

  const add = async () => {
    if (!newSlug || !newName) return;
    try {
      await api.post(`/admin/${kind}`, { slug: newSlug, name: newName, tagline: "", description: "", image: "" });
      setNewSlug(""); setNewName(""); onChange(); toast.success("Added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const upload = async (slug, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    await api.patch(`/admin/${kind}/${slug}`, { image: data.url });
    onChange(); toast.success("Hero updated");
  };

  const rename = async (slug, name) => { await api.patch(`/admin/${kind}/${slug}`, { name }); onChange(); };
  const saveAbout = async (slug) => { await api.patch(`/admin/${kind}/${slug}`, { about_html: aboutDraft }); setOpenAbout(null); onChange(); toast.success("About saved"); };
  const del = async (slug) => { if (!window.confirm(`Delete ${slug}?`)) return; await api.delete(`/admin/${kind}/${slug}`); onChange(); toast.success("Deleted"); };

  return (
    <section className="rounded-2xl border border-forest/10 bg-white p-5" data-testid={`tags-${kind}`}>
      <p className="font-display text-xl">{title}</p>
      <div className="mt-4 space-y-3">
        {items.map((i) => (
          <div key={i.slug} className="rounded-xl border border-forest/10 p-3" data-testid={`tag-row-${i.slug}`}>
            <div className="flex items-center gap-3">
              <img src={i.image || "/placeholder.png"} alt="" className="h-14 w-20 shrink-0 rounded object-cover bg-mist" />
              <div className="flex-1 min-w-0">
                <input defaultValue={i.name} onBlur={(e) => e.target.value !== i.name && rename(i.slug, e.target.value)}
                  className="w-full bg-transparent font-medium text-sm focus:outline-none" />
                <p className="text-[10px] text-noir/50">{i.slug}</p>
              </div>
              <label className="vyg-pill cursor-pointer !text-[11px]">
                Hero
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(i.slug, e.target.files[0])} />
              </label>
              {supportsAbout && (
                <button onClick={() => { setOpenAbout(openAbout === i.slug ? null : i.slug); setAboutDraft(i.about_html || ""); }}
                  className="vyg-pill !text-[11px]" data-testid={`toggle-about-${i.slug}`}>About</button>
              )}
              <button onClick={() => del(i.slug)} className="text-red-600 hover:bg-red-50 p-1.5 rounded"><Trash2 size={14}/></button>
            </div>
            {supportsAbout && openAbout === i.slug && (
              <div className="mt-3 border-t border-forest/10 pt-3">
                <p className="text-[11px] uppercase tracking-wider text-amber">About {i.name} (HTML)</p>
                <textarea rows={5} value={aboutDraft} onChange={(e) => setAboutDraft(e.target.value)}
                  data-testid={`about-input-${i.slug}`}
                  placeholder={`<p>What makes ${i.name} special…</p>`}
                  className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 font-mono text-xs" />
                <div className="mt-2 flex justify-end gap-2">
                  <button onClick={() => setOpenAbout(null)} className="vyg-pill !text-[11px]">Cancel</button>
                  <button onClick={() => saveAbout(i.slug)} className="vyg-cta !py-1.5 !text-xs" data-testid={`save-about-${i.slug}`}><Save size={12}/> Save about</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <input placeholder="slug" value={newSlug} onChange={(e) => setNewSlug(e.target.value)} data-testid={`new-${kind}-slug`}
          className="flex-1 rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm" />
        <input placeholder="Display name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid={`new-${kind}-name`}
          className="flex-1 rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm" />
        <button onClick={add} data-testid={`add-${kind}`} className="vyg-cta !py-2"><Plus size={14}/> Add</button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SEO Tags Admin — master list CRUD
// ---------------------------------------------------------------------------
function SeoTagsAdmin() {
  const [tags, setTags] = useState([]);
  const [label, setLabel] = useState("");
  const load = () => api.get("/admin/seo-tags").then(({ data }) => setTags(data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    const v = label.trim();
    if (!v) return;
    await api.post("/admin/seo-tags", { label: v });
    setLabel(""); load(); toast.success("Added");
  };
  const del = async (l) => {
    if (!window.confirm(`Delete "${l}"? It will also be removed from every property.`)) return;
    await api.delete(`/admin/seo-tags/${encodeURIComponent(l)}`);
    load(); toast.success("Deleted");
  };

  return (
    <section className="rounded-2xl border border-forest/10 bg-white p-5" data-testid="seo-tags-admin">
      <div className="flex items-center gap-2">
        <Tag size={18} className="text-forest"/>
        <p className="font-display text-xl">SEO tags · master list</p>
      </div>
      <p className="text-xs text-noir/60 mt-1">These tags appear at the top of each property's page. Edit a property to assign tags from this list.</p>

      <div className="mt-4 flex flex-wrap gap-2">
        {tags.map((t) => (
          <span key={t.label} data-testid={`seo-master-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="group inline-flex items-center gap-2 rounded-full border border-forest/20 bg-mist/40 px-3 py-1.5 text-sm text-forest">
            {t.label}
            <button onClick={() => del(t.label)} className="rounded-full text-noir/40 hover:text-red-600"><Trash2 size={12}/></button>
          </span>
        ))}
        {tags.length === 0 && <p className="text-sm text-noir/60">No tags yet.</p>}
      </div>

      <div className="mt-5 flex gap-2">
        <input value={label} onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="New tag label (e.g. Shoot-friendly)"
          data-testid="new-seo-master-input"
          className="flex-1 max-w-sm rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm" />
        <button onClick={add} data-testid="add-seo-master" className="vyg-cta !py-2"><Plus size={14}/> Add tag</button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Site config — social stats + policy HTML + contact (Instagram/WhatsApp)
// ---------------------------------------------------------------------------
function SiteConfigAdmin() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/config").then(({ data }) => setCfg(data)); }, []);
  if (!cfg) return <p className="text-sm text-noir/60">Loading…</p>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config", cfg);
      toast.success("Config saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="site-config" className="grid gap-4 rounded-2xl border border-forest/10 bg-white p-5 sm:grid-cols-2">
      <div className="sm:col-span-2 -mb-2">
        <p className="font-display text-lg">Contact channels</p>
        <p className="text-xs text-noir/60">Shown site-wide (header, footer, floating WhatsApp button).</p>
      </div>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-amber">Instagram handle (no @)</span>
        <input value={cfg.instagram_handle || ""} onChange={(e) => setCfg({ ...cfg, instagram_handle: e.target.value.replace(/^@/, "") })}
          data-testid="cfg-instagram" className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm" placeholder="vyagram.experiences" />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-amber">WhatsApp number (digits only, with country code)</span>
        <input value={cfg.whatsapp_number || ""} onChange={(e) => setCfg({ ...cfg, whatsapp_number: e.target.value.replace(/[^\d]/g, "") })}
          data-testid="cfg-whatsapp-number" className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm" placeholder="919355012999" />
      </label>
      <label className="block sm:col-span-2">
        <span className="text-[11px] uppercase tracking-wider text-amber">WhatsApp display (for footer / buttons)</span>
        <input value={cfg.whatsapp_display || ""} onChange={(e) => setCfg({ ...cfg, whatsapp_display: e.target.value })}
          data-testid="cfg-whatsapp-display" className="mt-1 w-full max-w-sm rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm" placeholder="+91 93550 12999" />
      </label>
      <label className="block sm:col-span-2">
        <span className="text-[11px] uppercase tracking-wider text-amber">Support email (footer · contact · policies)</span>
        <input
          type="email"
          value={cfg.support_email || ""}
          onChange={(e) => setCfg({ ...cfg, support_email: e.target.value.trim() })}
          data-testid="cfg-support-email"
          className="mt-1 w-full max-w-sm rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm"
          placeholder="stay@vyagram.com"
        />
        <span className="mt-1 block text-[11px] text-noir/55">
          Used wherever the site shows a public email. Change once — every page picks it up.
        </span>
      </label>

      <div className="sm:col-span-2 mt-3 border-t border-forest/10 pt-4 -mb-2">
        <p className="font-display text-lg">Social validation (global)</p>
      </div>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-amber">Superhost since (YYYY-MM-DD)</span>
        <input value={cfg.superhost_since || ""} onChange={(e) => setCfg({ ...cfg, superhost_since: e.target.value })}
          data-testid="cfg-superhost-since" className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm" />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-amber">Total stays hosted (portfolio)</span>
        <input type="number" value={cfg.total_stays_hosted || 0} onChange={(e) => setCfg({ ...cfg, total_stays_hosted: Number(e.target.value) })}
          data-testid="cfg-stays" className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm" />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-amber">Five-star reviews (portfolio)</span>
        <input type="number" value={cfg.five_star_reviews || 0} onChange={(e) => setCfg({ ...cfg, five_star_reviews: Number(e.target.value) })}
          data-testid="cfg-fivestar" className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm" />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-amber">Airbnb markup % (global)</span>
        <input type="number" value={cfg.airbnb_markup_pct || 0} onChange={(e) => setCfg({ ...cfg, airbnb_markup_pct: Number(e.target.value) })}
          data-testid="cfg-markup" className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm" />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-amber">Website markup % (default · applied to all properties)</span>
        <input
          type="number"
          step="0.5"
          value={cfg.website_markup_pct ?? 0}
          onChange={(e) => setCfg({ ...cfg, website_markup_pct: Number(e.target.value) })}
          data-testid="cfg-website-markup"
          className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm"
        />
        <span className="mt-1 block text-[11px] text-noir/55">
          Adds an invisible % over Hostify's price across the site. Per-property override available in each property editor.
        </span>
      </label>
      <label className="block sm:col-span-2">
        <span className="text-[11px] uppercase tracking-wider text-amber">Privacy policy (HTML)</span>
        <textarea rows={6} value={cfg.privacy_html || ""} onChange={(e) => setCfg({ ...cfg, privacy_html: e.target.value })}
          className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 font-mono text-xs" />
      </label>
      <label className="block sm:col-span-2">
        <span className="text-[11px] uppercase tracking-wider text-amber">Stay policy (HTML · shown at checkout consent)</span>
        <textarea rows={6} value={cfg.stay_policy_html || ""} onChange={(e) => setCfg({ ...cfg, stay_policy_html: e.target.value })}
          data-testid="cfg-stay-policy"
          placeholder="Cancellation window, quiet hours, extra guest fees, pet policy, smoking policy, damage deposit, etc."
          className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 font-mono text-xs" />
      </label>

      {/* Shoots — intro HTML + editable media gallery (photos + videos) */}
      <div className="sm:col-span-2 mt-3 border-t border-forest/10 pt-4 -mb-2">
        <p className="font-display text-lg">Shoots page — portfolio gallery</p>
        <p className="text-xs text-noir/60">Upload images or MP4s, or paste YouTube/Vimeo links. They appear in the gallery grid below the form on /shoots.</p>
      </div>
      <label className="block sm:col-span-2">
        <span className="text-[11px] uppercase tracking-wider text-amber">Shoots intro (HTML · above the inquiry form)</span>
        <textarea rows={4} value={cfg.shoot_intro_html || ""} onChange={(e) => setCfg({ ...cfg, shoot_intro_html: e.target.value })}
          data-testid="cfg-shoot-intro"
          className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 font-mono text-xs" />
      </label>
      <div className="sm:col-span-2">
        <ShootMediaEditor media={cfg.shoot_media || []} onChange={(next) => setCfg({ ...cfg, shoot_media: next })} />
      </div>

      <label className="block sm:col-span-2">
        <span className="text-[11px] uppercase tracking-wider text-amber">Terms of service (HTML)</span>
        <textarea rows={6} value={cfg.terms_html || ""} onChange={(e) => setCfg({ ...cfg, terms_html: e.target.value })}
          className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 font-mono text-xs" />
      </label>

      <div className="sm:col-span-2 mt-3 border-t border-forest/10 pt-4 -mb-2">
        <p className="font-display text-lg">Analytics</p>
        <p className="text-xs text-noir/60">Leave blank to disable. Scripts inject automatically when IDs are set.</p>
      </div>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-amber">Google Analytics · Measurement ID (G-…)</span>
        <input value={cfg.ga_measurement_id || ""} onChange={(e) => setCfg({ ...cfg, ga_measurement_id: e.target.value.trim() })}
          data-testid="cfg-ga-id" className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm font-mono" placeholder="G-XXXXXXXXXX" />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-amber">Meta (Facebook) Pixel ID</span>
        <input value={cfg.meta_pixel_id || ""} onChange={(e) => setCfg({ ...cfg, meta_pixel_id: e.target.value.trim() })}
          data-testid="cfg-meta-pixel" className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm font-mono" placeholder="1234567890123456" />
      </label>

      <div className="sm:col-span-2 mt-3 border-t border-forest/10 pt-4 -mb-2">
        <p className="font-display text-lg">Favicon</p>
        <p className="text-xs text-noir/60">SVG or PNG. Recommended 64×64 with a forest backdrop. Leave URL blank to fall back to the default leaf + VE monogram.</p>
      </div>
      <FaviconUploader value={cfg.favicon_url || ""} onChange={(v) => setCfg({ ...cfg, favicon_url: v })} />

      <div className="sm:col-span-2 flex justify-end">
        <button onClick={save} disabled={saving} className="vyg-cta" data-testid="cfg-save">
          <Save size={14}/> {saving ? "Saving…" : "Save config"}
        </button>
      </div>
    </div>
  );
}

/**
 * FaviconUploader — paste a URL or upload an SVG/PNG. Stored in
 * site_config.favicon_url and injected into the public site head via
 * `useSiteConfig`. Used to brand browser tabs without a redeploy.
 */
function FaviconUploader({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Favicon uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="sm:col-span-2 flex flex-wrap items-center gap-3" data-testid="favicon-uploader">
      <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-forest/15 bg-mist/40">
        {value
          ? <img src={value} alt="Favicon preview" className="max-h-10 max-w-10 object-contain" data-testid="favicon-preview" />
          : <span className="text-[10px] text-noir/50">default</span>}
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Paste a public URL or upload below"
        className="inp flex-1 min-w-[16rem]"
        data-testid="favicon-url-input"
      />
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-forest/20 bg-white px-3 py-1.5 text-sm text-forest hover:bg-mist/40" data-testid="favicon-upload-btn">
        <Upload size={14}/> {busy ? "Uploading…" : "Upload"}
        <input type="file" accept="image/svg+xml,image/png,image/x-icon,image/jpeg" className="hidden" disabled={busy}
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      </label>
      {value && (
        <button type="button" onClick={() => onChange("")} className="text-xs text-noir/60 hover:text-red-600" data-testid="favicon-clear">Reset to default</button>
      )}
    </div>
  );
}

/**
 * NotificationsAdmin — per-form email recipients (contact / shoot / booking)
 * AND per-event on/off toggles. Booking events fan out into intent_created,
 * payment_confirmed, payment_sync_failed, payment_signature_failed, etc.
 * Reads + writes /admin/config (keys: email_recipients + email_events).
 */
const BOOKING_EVENTS = [
  { key: "payment_confirmed", label: "Payment confirmed", help: "Successful payment + Hostify reservation written." },
  { key: "payment_sync_failed", label: "Hostify sync failed", help: "Payment OK but reservation didn't write to Hostify. Critical." },
  { key: "payment_signature_failed", label: "Payment signature failed", help: "Razorpay signature mismatch — possible fraud / man-in-the-middle." },
  { key: "intent_created", label: "Booking intent created", help: "User started a booking. Useful for funnel tracking." },
  { key: "form_touched", label: "Booking form touched", help: "User typed into the form (high noise — usually muted)." },
];

function NotificationsAdmin() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/config").then(({ data }) => setCfg(data)); }, []);
  if (!cfg) return <p className="text-sm text-noir/60">Loading…</p>;

  const recipients = cfg.email_recipients || {};
  const events = cfg.email_events || {};
  const setRecipient = (k, v) =>
    setCfg({ ...cfg, email_recipients: { ...recipients, [k]: v } });
  const setEvent = (channel, eventKey, on) => {
    const next = {
      ...events,
      [channel]: { ...(events[channel] || {}), [eventKey]: on },
    };
    setCfg({ ...cfg, email_events: next });
  };
  const channelEnabled = (channel, eventKey) => {
    const v = events?.[channel]?.[eventKey];
    return v === undefined ? true : !!v;
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config", {
        email_recipients: cfg.email_recipients,
        email_events: cfg.email_events,
      });
      toast.success("Notifications saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="notifications-admin">
      <div className="rounded-2xl border border-forest/10 bg-white p-5">
        <p className="font-display text-lg">Email recipients · per form</p>
        <p className="text-xs text-noir/60 mt-1">
          A separate inbox can be set for each form so the right team gets the alert. Multiple recipients?
          Comma-separate emails. Leave a field blank to fall back to the default address configured in the
          deployment.
        </p>
        <div className="mt-4 space-y-4">
          {[
            { key: "contact", label: "Contact form", help: "From the /contact page and the floating chat trigger." },
            { key: "shoot_inquiry", label: "Creators & Shoots form", help: "From the /shoots inquiry form." },
            { key: "booking", label: "Booking events", help: "Razorpay flow and Hostify reservation write-back." },
          ].map((f) => (
            <label key={f.key} className="block" data-testid={`notif-row-${f.key}`}>
              <span className="text-[11px] uppercase tracking-wider text-amber">{f.label}</span>
              <input
                type="text"
                value={recipients[f.key] || ""}
                onChange={(e) => setRecipient(f.key, e.target.value.trim())}
                placeholder="ops@vyagram.com, hello@vyagram.com"
                data-testid={`notif-input-${f.key}`}
                className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-noir/60">{f.help}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-forest/10 bg-white p-5">
        <p className="font-display text-lg">Booking events · what triggers an email</p>
        <p className="text-xs text-noir/60 mt-1">
          Toggle individual events on/off. Failure events stay on by default — those are the ones that
          matter most. "Form touched" is high-noise and usually muted.
        </p>
        <div className="mt-4 divide-y divide-forest/10">
          {BOOKING_EVENTS.map((ev) => {
            const on = channelEnabled("booking", ev.key);
            return (
              <div key={ev.key} className="flex items-center justify-between gap-4 py-3" data-testid={`notif-event-${ev.key}`}>
                <div>
                  <p className="text-sm font-medium text-noir">{ev.label}</p>
                  <p className="text-xs text-noir/60">{ev.help}</p>
                </div>
                <ToggleSwitch
                  checked={on}
                  onChange={(v) => setEvent("booking", ev.key, v)}
                  testId={`notif-toggle-booking-${ev.key}`}
                  label={ev.label}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-forest/10 bg-white p-5">
        <p className="font-display text-lg">Form-submission events</p>
        <p className="text-xs text-noir/60 mt-1">
          Master switches for the contact and shoots forms. Useful when you want to silence one channel
          without clearing its recipients.
        </p>
        <div className="mt-4 divide-y divide-forest/10">
          {[
            { channel: "contact", label: "Contact form submitted" },
            { channel: "shoot_inquiry", label: "Shoot inquiry submitted" },
          ].map((row) => {
            const on = channelEnabled(row.channel, "submit");
            return (
              <div key={row.channel} className="flex items-center justify-between gap-4 py-3" data-testid={`notif-event-${row.channel}-submit`}>
                <p className="text-sm font-medium text-noir">{row.label}</p>
                <ToggleSwitch
                  checked={on}
                  onChange={(v) => setEvent(row.channel, "submit", v)}
                  testId={`notif-toggle-${row.channel}-submit`}
                  label={row.label}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="vyg-cta" data-testid="notif-save">
          <Save size={14}/> {saving ? "Saving…" : "Save notifications"}
        </button>
      </div>
    </div>
  );
}

/**
 * ToggleSwitch — accessible iOS-style toggle. Used in the Notifications tab
 * for per-event on/off pickers. Kept inline because it's the only place we
 * need it and the visual is intentionally distinct from a checkbox.
 */
function ToggleSwitch({ checked, onChange, testId, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-forest" : "bg-noir/15"}`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-1"}`} />
    </button>
  );
}

/**
 * CurationAdmin — per-collection property ordering. Each collection renders
 * as a card showing the properties assigned to it in their current display
 * order. The admin reorders with up/down arrows; saving PUTs a slug list to
 * /admin/collections/{slug}/order which the public /properties endpoint
 * respects when filtering by that collection.
 */
function CurationAdmin() {
  const [collections, setCollections] = useState([]);
  const [locations, setLocations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [collectionOrders, setCollectionOrders] = useState({});
  const [locationOrders, setLocationOrders] = useState({});
  const [bucketOrders, setBucketOrders] = useState({});
  const [saving, setSaving] = useState("");

  const GUEST_BUCKETS = ["2", "4", "6", "8", "10", "12", "14+"];

  useEffect(() => {
    Promise.all([
      api.get("/admin/collections"),
      api.get("/admin/locations"),
      api.get("/properties"),
      api.get("/admin/guest-bucket-order"),
    ]).then(([c, l, p, g]) => {
      setCollections(c.data);
      setLocations(l.data);
      setProperties(p.data);
      // Collection orders
      const cNext = {};
      for (const col of c.data) {
        const members = p.data.filter((pr) => (pr.collections || []).includes(col.slug)).map((pr) => pr.slug);
        const saved = (col.property_order || []).filter((s) => members.includes(s));
        const leftover = members.filter((s) => !saved.includes(s));
        cNext[col.slug] = [...saved, ...leftover];
      }
      setCollectionOrders(cNext);
      // Location orders
      const lNext = {};
      for (const loc of l.data) {
        const members = p.data.filter((pr) => pr.location_slug === loc.slug).map((pr) => pr.slug);
        const saved = (loc.property_order || []).filter((s) => members.includes(s));
        const leftover = members.filter((s) => !saved.includes(s));
        lNext[loc.slug] = [...saved, ...leftover];
      }
      setLocationOrders(lNext);
      // Guest bucket orders — every bucket starts with the full property list
      const allSlugs = p.data.map((pr) => pr.slug);
      const bNext = {};
      const stored = g.data?.guest_bucket_order || {};
      for (const b of GUEST_BUCKETS) {
        const saved = (stored[b] || []).filter((s) => allSlugs.includes(s));
        const leftover = allSlugs.filter((s) => !saved.includes(s));
        bNext[b] = [...saved, ...leftover];
      }
      setBucketOrders(bNext);
    });
  }, []);

  const displayName = (slug) => properties.find((p) => p.slug === slug)?.display_name || slug;

  const moveIn = (map, setMap, key, idx, dir) => {
    setMap((cur) => {
      const list = [...(cur[key] || [])];
      const target = idx + dir;
      if (target < 0 || target >= list.length) return cur;
      [list[idx], list[target]] = [list[target], list[idx]];
      return { ...cur, [key]: list };
    });
  };

  const saveCollection = async (slug) => {
    setSaving(`c:${slug}`);
    try {
      await api.put(`/admin/collections/${slug}/order`, { slugs: collectionOrders[slug] || [] });
      toast.success(`Saved · ${slug} order`);
    } catch { toast.error("Could not save"); }
    finally { setSaving(""); }
  };
  const saveLocation = async (slug) => {
    setSaving(`l:${slug}`);
    try {
      await api.put(`/admin/locations/${slug}/order`, { slugs: locationOrders[slug] || [] });
      toast.success(`Saved · ${slug} order`);
    } catch { toast.error("Could not save"); }
    finally { setSaving(""); }
  };
  const saveBuckets = async () => {
    setSaving("buckets");
    try {
      await api.put("/admin/guest-bucket-order", { orders: bucketOrders });
      toast.success("Saved · guest-bucket orders");
    } catch { toast.error("Could not save"); }
    finally { setSaving(""); }
  };

  if (!collections.length) return <p className="text-sm text-noir/60">Loading…</p>;

  const Row = ({ list, onUp, onDown, kind }) => (
    <ul className="mt-4 space-y-2">
      {list.map((slug, i) => (
        <li key={slug} data-testid={`curation-row-${kind}-${slug}`} className="flex items-center gap-3 rounded-lg border border-forest/10 bg-mist/30 px-3 py-2 text-sm">
          <span className="w-6 text-right font-display text-lg text-forest">{i + 1}</span>
          <span className="flex-1 truncate">{displayName(slug)}</span>
          <span className="text-xs text-noir/50">{slug}</span>
          <button onClick={() => onUp(i)} disabled={i === 0} className="rounded p-1 text-forest hover:bg-white disabled:opacity-30" aria-label="Move up" data-testid={`curation-${kind}-up-${slug}`}>▲</button>
          <button onClick={() => onDown(i)} disabled={i === list.length - 1} className="rounded p-1 text-forest hover:bg-white disabled:opacity-30" aria-label="Move down" data-testid={`curation-${kind}-down-${slug}`}>▼</button>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="space-y-8" data-testid="curation-admin">
      <div className="rounded-2xl border border-forest/10 bg-mist/40 p-5 text-sm text-forest/90">
        <p className="font-display text-lg">Curated display order</p>
        <p className="mt-1 text-xs text-noir/70">
          Pick which property shows first inside each curated collection, each location page,
          and for each guest-count bracket on the search results. Precedence on /properties
          search: collection &gt; location &gt; guest-bucket. Use the ▲/▼ arrows and save per section.
        </p>
      </div>

      {/* COLLECTIONS */}
      <div>
        <p className="font-display text-xl">By collection</p>
        <div className="mt-3 space-y-4">
          {collections.map((col) => {
            const list = collectionOrders[col.slug] || [];
            return (
              <div key={col.slug} className="rounded-2xl border border-forest/10 bg-white p-5" data-testid={`curation-collection-${col.slug}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <div>
                    <p className="font-display text-lg">{col.name}</p>
                    <p className="text-xs text-noir/60">{col.tagline}</p>
                  </div>
                  <button onClick={() => saveCollection(col.slug)} disabled={saving === `c:${col.slug}`} className="vyg-pill" data-testid={`curation-save-${col.slug}`}>
                    <Save size={12}/> {saving === `c:${col.slug}` ? "Saving…" : "Save order"}
                  </button>
                </div>
                {list.length === 0 ? <p className="mt-4 text-sm text-noir/60">No properties in this collection yet.</p>
                  : <Row list={list} kind={`c-${col.slug}`} onUp={(i) => moveIn(collectionOrders, setCollectionOrders, col.slug, i, -1)} onDown={(i) => moveIn(collectionOrders, setCollectionOrders, col.slug, i, 1)} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* LOCATIONS */}
      <div>
        <p className="font-display text-xl">By location</p>
        <div className="mt-3 space-y-4">
          {locations.map((loc) => {
            const list = locationOrders[loc.slug] || [];
            return (
              <div key={loc.slug} className="rounded-2xl border border-forest/10 bg-white p-5" data-testid={`curation-location-${loc.slug}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <div>
                    <p className="font-display text-lg">{loc.name}</p>
                    <p className="text-xs text-noir/60">{loc.description?.slice(0, 80)}…</p>
                  </div>
                  <button onClick={() => saveLocation(loc.slug)} disabled={saving === `l:${loc.slug}`} className="vyg-pill" data-testid={`curation-save-location-${loc.slug}`}>
                    <Save size={12}/> {saving === `l:${loc.slug}` ? "Saving…" : "Save order"}
                  </button>
                </div>
                {list.length === 0 ? <p className="mt-4 text-sm text-noir/60">No properties in this location yet.</p>
                  : <Row list={list} kind={`l-${loc.slug}`} onUp={(i) => moveIn(locationOrders, setLocationOrders, loc.slug, i, -1)} onDown={(i) => moveIn(locationOrders, setLocationOrders, loc.slug, i, 1)} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* GUEST BUCKETS */}
      <div>
        <div className="flex items-baseline justify-between">
          <div>
            <p className="font-display text-xl">By guest count</p>
            <p className="text-xs text-noir/60 mt-1">Which properties show first when a visitor searches for 2, 4, 6, 8, 10, 12 or 14+ guests. Only available properties are returned; this controls the order.</p>
          </div>
          <button onClick={saveBuckets} disabled={saving === "buckets"} className="vyg-cta" data-testid="curation-save-buckets">
            <Save size={14}/> {saving === "buckets" ? "Saving…" : "Save all guest orders"}
          </button>
        </div>
        <div className="mt-4 space-y-4">
          {GUEST_BUCKETS.map((b) => {
            const list = bucketOrders[b] || [];
            return (
              <div key={b} className="rounded-2xl border border-forest/10 bg-white p-5" data-testid={`curation-bucket-${b}`}>
                <p className="font-display text-lg">{b === "14+" ? "14+ guests" : `Up to ${b} guests`}</p>
                {list.length === 0 ? <p className="mt-3 text-sm text-noir/60">No properties yet.</p>
                  : <Row list={list} kind={`b-${b}`} onUp={(i) => moveIn(bucketOrders, setBucketOrders, b, i, -1)} onDown={(i) => moveIn(bucketOrders, setBucketOrders, b, i, 1)} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    intent: "bg-mist text-forest",
    attempted: "bg-mist text-forest",
    order_created: "bg-gold/20 text-amber",
    paid: "bg-forest text-parchment",
    paid_sync_failed: "bg-red-100 text-red-700",
    payment_failed: "bg-red-100 text-red-700",
  };
  return <span className={`rounded-full px-2 py-0.5 text-[11px] ${map[status] || "bg-mist text-forest"}`}>{status}</span>;
}

// ---------------------------------------------------------------------------
// Reviews Admin — source dropdown, CSV export + import
// ---------------------------------------------------------------------------
const SOURCE_OPTIONS = ["airbnb", "booking.com", "google", "vrbo", "direct", "manual", "other"];

function ReviewsAdmin({ reviews, properties, onChange }) {
  const [form, setForm] = useState({
    property_slug: properties[0]?.slug || "",
    reviewer_first_name: "", review_month_year: "", rating: 5, title: "", quote: "",
    source_platform: "airbnb", featured: false, approved_for_website: true,
  });
  const [importing, setImporting] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/reviews", form);
      toast.success("Review added");
      setForm({ ...form, reviewer_first_name: "", review_month_year: "", title: "", quote: "" });
      onChange();
    } catch { toast.error("Failed to add review"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Deleted"); onChange();
  };

  const toggleFeatured = async (r) => {
    try {
      await api.patch(`/admin/reviews/${r.id}`, { featured: !r.featured });
      toast.success(r.featured ? "Removed from featured" : "Marked as featured");
      onChange();
    } catch { toast.error("Couldn't update review"); }
  };

  const exportCsv = () => downloadCsv("/admin/reviews/export.csv", "reviews.csv");

  const importCsv = async (file) => {
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/reviews/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported · ${data.inserted} new · ${data.updated} updated${data.errors?.length ? ` · ${data.errors.length} errors` : ""}`);
      if (data.errors?.length) console.warn("import errors:", data.errors);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
      <form onSubmit={add} className="rounded-2xl border border-forest/10 bg-white p-5 self-start" data-testid="review-add-form">
        <p className="font-display text-xl">Add review</p>
        <div className="mt-4 space-y-3 text-sm">
          <select value={form.property_slug} onChange={(e) => setForm({ ...form, property_slug: e.target.value })} className="w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2">
            {properties.map((p) => <option key={p.slug} value={p.slug}>{p.display_name}</option>)}
          </select>
          <input required placeholder="Reviewer first name" value={form.reviewer_first_name} onChange={(e) => setForm({ ...form, reviewer_first_name: e.target.value })} className="w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2" />
          <input placeholder="Month/Year e.g. Oct 2025" value={form.review_month_year} onChange={(e) => setForm({ ...form, review_month_year: e.target.value })} className="w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2" />
          <input type="number" min={1} max={5} step={0.1} value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} className="w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2" />
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-amber">Source</span>
            <select value={form.source_platform} onChange={(e) => setForm({ ...form, source_platform: e.target.value })}
              data-testid="review-source-select"
              className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2 capitalize">
              {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <input placeholder="Title (optional)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2" />
          <textarea required placeholder="Quote" rows={4} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2" />
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
          <button className="vyg-cta w-full"><Plus size={16}/> Add review</button>
        </div>
      </form>

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-noir/70">{reviews.length} reviews on file. CSV import will add new + update rows matched by id.</p>
          <div className="flex gap-2">
            <button onClick={exportCsv} data-testid="export-reviews-csv"
              className="inline-flex items-center gap-1 rounded-full border border-forest/20 bg-white px-3 py-1.5 text-sm text-forest hover:bg-mist/40">
              <Download size={14}/> Export CSV
            </button>
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-forest/20 bg-white px-3 py-1.5 text-sm text-forest hover:bg-mist/40" data-testid="import-reviews-csv">
              <Upload size={14}/> {importing ? "Importing…" : "Import CSV"}
              <input type="file" accept=".csv,text/csv" className="hidden" disabled={importing}
                onChange={(e) => e.target.files?.[0] && importCsv(e.target.files[0])} />
            </label>
          </div>
        </div>

        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="flex items-start justify-between gap-3 rounded-xl border border-forest/10 bg-white p-4" data-testid={`admin-review-${r.id}`}>
              <div>
                <p className="font-medium text-sm">{r.reviewer_first_name} · <span className="text-noir/60">{r.property_slug}</span></p>
                <p className="text-xs text-amber">{r.rating} ★ · {r.review_month_year} · {r.source_platform}{r.featured ? " · featured" : ""}</p>
                {r.title && <p className="mt-1 font-display text-lg">{r.title}</p>}
                <p className="mt-1 text-sm text-noir/80">"{r.quote}"</p>
              </div>
              <button onClick={() => del(r.id)} className="text-red-600 hover:bg-red-50 p-2 rounded"><Trash2 size={16}/></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ShootMediaEditor — admin-editable list of shoot gallery items. Each entry
// is { url, type: 'image'|'video', caption? }. Supports in-line file upload
// and URL paste (handy for YouTube / Vimeo embeds).
// ---------------------------------------------------------------------------
function ShootMediaEditor({ media, onChange }) {
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");

  const add = (u, type) => {
    if (!u) return;
    onChange([...(media || []), { url: u, type: type || (/\.(mp4|mov|webm)(\?|$)|youtube\.com|youtu\.be|vimeo\.com/i.test(u) ? "video" : "image"), caption }]);
    setUrl(""); setCaption("");
  };

  const upload = async (file) => {
    const type = file.type.startsWith("video/") ? "video" : "image";
    if (type === "video") {
      const duration = await new Promise((r) => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => r(v.duration || 0);
        v.onerror = () => r(0);
        v.src = URL.createObjectURL(file);
      });
      if (duration > 180.5) { toast.error(`Video is ${Math.round(duration)}s — please keep under 3 minutes for this gallery.`); return; }
    }
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    add(data.url, type);
    toast.success("Added to shoot gallery");
  };

  const move = (i, dir) => {
    const next = [...media]; const swap = i + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[i], next[swap]] = [next[swap], next[i]];
    onChange(next);
  };
  const remove = (i) => onChange(media.filter((_, j) => j !== i));
  const updateCaption = (i, c) => {
    const next = [...media]; next[i] = { ...next[i], caption: c }; onChange(next);
  };

  return (
    <section className="rounded-2xl border border-forest/10 bg-parchment p-5" data-testid="shoot-media-editor">
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-display text-lg">Shoot gallery items ({media?.length || 0})</p>
        <label className="vyg-pill cursor-pointer ml-auto" data-testid="shoot-upload">
          <Upload size={14}/> Upload image or video
          <input type="file" accept="image/*,video/*" className="hidden"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="Or paste a YouTube / Vimeo / image URL"
          data-testid="shoot-url-input"
          className="rounded-lg border border-forest/20 bg-white px-3 py-2 text-sm" />
        <input value={caption} onChange={(e) => setCaption(e.target.value)}
          placeholder="Caption (optional · shown on hover)"
          data-testid="shoot-caption-input"
          className="rounded-lg border border-forest/20 bg-white px-3 py-2 text-sm" />
      </div>
      <button type="button" onClick={() => add(url.trim())} data-testid="shoot-add-url"
        className="vyg-pill mt-2"><Plus size={14}/> Add to gallery</button>

      <div className="mt-5 grid gap-3 sm:grid-cols-3 md:grid-cols-4">
        {(media || []).map((m, i) => {
          const u = typeof m === "string" ? m : m.url;
          const t = typeof m === "object" && m.type ? m.type : (/\.(mp4|mov|webm)(\?|$)|youtube|vimeo/i.test(u) ? "video" : "image");
          const cap = typeof m === "object" ? (m.caption || "") : "";
          return (
            <div key={u || `media-${i}`} data-testid={`shoot-tile-admin-${i}`} className="rounded-xl border border-forest/10 bg-white p-2">
              <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-noir/10">
                {t === "video"
                  ? <div className="flex h-full w-full items-center justify-center text-xs text-white bg-noir/80 font-mono">{/youtube|vimeo/i.test(u) ? "YouTube / Vimeo" : "Video"}</div>
                  : <img src={u} alt="" className="h-full w-full object-cover" />}
              </div>
              <input defaultValue={cap} onBlur={(e) => e.target.value !== cap && updateCaption(i, e.target.value)}
                placeholder="Caption"
                className="mt-2 w-full bg-transparent text-[11px] text-noir/70 focus:outline-none" />
              <div className="mt-1 flex items-center justify-between text-xs">
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} className="px-1 text-noir/60">▲</button>
                  <button onClick={() => move(i, 1)} className="px-1 text-noir/60">▼</button>
                </div>
                <button onClick={() => remove(i)} className="text-red-600 hover:bg-red-50 p-1 rounded"><Trash2 size={12}/></button>
              </div>
            </div>
          );
        })}
        {(!media || media.length === 0) && <p className="col-span-full text-sm text-noir/50">No shoot gallery items yet.</p>}
      </div>
    </section>
  );
}


function GlobalFaqsAdmin() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ group: "Booking & Payment", question: "", answer: "" });
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroup, setNewGroup] = useState("");
  const load = () => api.get("/admin/faqs", { params: { scope: "global" } }).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!draft.question.trim() || !draft.answer.trim()) { toast.error("Question and answer required"); return; }
    await api.post("/admin/faqs", { scope: "global", ...draft, order: items.length });
    setDraft({ group: draft.group, question: "", answer: "" });
    load();
  };
  const update = async (id, patch) => { await api.patch(`/admin/faqs/${id}`, patch); load(); };
  const del = async (id) => { if (!window.confirm("Delete this FAQ?")) return; await api.delete(`/admin/faqs/${id}`); load(); };

  // Group view for easy scanning
  const grouped = items.reduce((acc, f) => { (acc[f.group || "General"] ||= []).push(f); return acc; }, {});
  // Categories — union of seeded defaults + every group already in use, so
  // the admin can pick from a known set without re-typing.
  const SEED_GROUPS = ["Booking & Payment", "Stay & Check-in", "Shoots & Business", "Policies & Cancellation", "General"];
  const categories = Array.from(new Set([...SEED_GROUPS, ...Object.keys(grouped)])).sort();

  const onCategoryChange = (val) => {
    if (val === "__new__") {
      setShowNewGroup(true);
      setNewGroup("");
      return;
    }
    setDraft({ ...draft, group: val });
  };
  const commitNewGroup = () => {
    const trimmed = newGroup.trim();
    if (!trimmed) { setShowNewGroup(false); return; }
    setDraft({ ...draft, group: trimmed });
    setShowNewGroup(false);
    setNewGroup("");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[360px_1fr]" data-testid="global-faqs-admin">
      <form onSubmit={(e) => { e.preventDefault(); add(); }} className="rounded-2xl border border-forest/10 bg-white p-5 self-start">
        <p className="font-display text-xl">Add FAQ</p>
        <p className="text-xs text-noir/60 mt-1">These appear on the public /faq page in the order you add them.</p>
        <div className="mt-4 space-y-3 text-sm">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-amber">Category</span>
            {showNewGroup ? (
              <div className="mt-1 flex gap-2">
                <input
                  autoFocus
                  value={newGroup}
                  onChange={(e) => setNewGroup(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitNewGroup(); } if (e.key === "Escape") { setShowNewGroup(false); } }}
                  placeholder="New category name"
                  data-testid="faq-new-group-input"
                  className="flex-1 rounded-lg border border-forest/20 bg-parchment px-3 py-2"
                />
                <button type="button" onClick={commitNewGroup} className="vyg-pill" data-testid="faq-new-group-confirm">Add</button>
                <button type="button" onClick={() => setShowNewGroup(false)} className="rounded-full border border-forest/20 px-3 py-1.5 text-xs text-noir/70 hover:bg-mist/40">Cancel</button>
              </div>
            ) : (
              <select
                value={categories.includes(draft.group) ? draft.group : draft.group || ""}
                onChange={(e) => onCategoryChange(e.target.value)}
                data-testid="faq-group-input"
                className="mt-1 w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2"
              >
                {!categories.includes(draft.group) && draft.group && (
                  <option value={draft.group}>{draft.group}</option>
                )}
                {categories.map((g) => (<option key={g} value={g}>{g}</option>))}
                <option value="__new__">+ Add new category…</option>
              </select>
            )}
          </div>
          <input placeholder="Question" value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })}
            data-testid="faq-question-input"
            className="w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2" />
          <textarea placeholder="Answer" rows={5} value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
            data-testid="faq-answer-input"
            className="w-full rounded-lg border border-forest/20 bg-parchment px-3 py-2" />
          <button data-testid="add-global-faq" className="vyg-cta w-full"><Plus size={16}/> Add FAQ</button>
        </div>
      </form>

      <div className="space-y-6">
        {Object.keys(grouped).length === 0 && <p className="text-sm text-noir/60">No FAQs yet. Defaults will be seeded on first public load.</p>}
        {Object.entries(grouped).map(([group, gItems]) => (
          <div key={group}>
            <p className="font-display text-lg text-forest">{group}</p>
            <div className="mt-2 space-y-2">
              {gItems.map((f) => (
                <div key={f.id} className="rounded-xl border border-forest/10 bg-white p-4" data-testid={`global-faq-${f.id}`}>
                  <input defaultValue={f.question}
                    onBlur={(e) => e.target.value !== f.question && update(f.id, { question: e.target.value })}
                    className="w-full bg-transparent font-medium focus:outline-none" />
                  <textarea defaultValue={f.answer} rows={2}
                    onBlur={(e) => e.target.value !== f.answer && update(f.id, { answer: e.target.value })}
                    className="mt-2 w-full bg-transparent text-sm text-noir/80 focus:outline-none resize-none" />
                  <div className="mt-2 flex items-center justify-between">
                    <select
                      defaultValue={f.group}
                      onChange={(e) => e.target.value !== f.group && update(f.id, { group: e.target.value })}
                      className="bg-transparent text-xs text-amber focus:outline-none"
                      data-testid={`global-faq-group-${f.id}`}
                    >
                      {!categories.includes(f.group) && f.group && <option value={f.group}>{f.group}</option>}
                      {categories.map((g) => (<option key={g} value={g}>{g}</option>))}
                    </select>
                    <button onClick={() => del(f.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded"><Trash2 size={14}/></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
